import React, { useEffect, useRef, useState } from 'react'
import { Ban, ChevronDown, RotateCcw, Star } from 'lucide-react'
import { BrokerType } from '@numeo/types-core'
import { cn } from 'utils/cn'
import { Button } from 'components/ds/Button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'components/ds/Modal'
import { useBrokerStatusAction } from 'hooks/useBrokerStatusAction'

interface BrokerStatusChipProps {
    brokerMc: string | null | undefined
    /** Alternate key for MC-less sources (box-truck Sylectus): favorite/blacklist by broker email. */
    brokerEmail?: string | null
    brokerName?: string | null
}

/**
 * Colour-coded broker-status control shown next to the broker name in the load-hub expanded row.
 *   • blacklisted → red   • favorite → green   • neither → neutral "Mark" chip.
 * Clicking opens a small menu (Favorite / Blacklist / Remove). Blacklisting asks for confirmation.
 * Mutation logic lives in useBrokerStatusAction.
 */
export const BrokerStatusChip: React.FC<BrokerStatusChipProps> = ({ brokerMc, brokerEmail, brokerName }) => {
    const { status, isBlacklisted, isFavorite, loading, applyStatus } = useBrokerStatusAction({ brokerMc, brokerEmail, brokerName })
    const [menuOpen, setMenuOpen] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const wrapRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!menuOpen) return
        const onClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false)
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [menuOpen])

    if (!brokerMc && !brokerEmail) return null
    // Email-keyed creation requires a company name server-side — with neither a name nor an existing
    // row to update, every offered action could only 400, so don't offer any.
    if (!brokerMc && !brokerName && !status) return null

    const label = brokerName || (brokerMc ? `MC# ${brokerMc}` : brokerEmail!)

    const chip = isBlacklisted
        ? {
              text: 'Blocked',
              icon: <Ban className="w-3 h-3" />,
              cls: 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-500/10 dark:border-red-500/20',
          }
        : isFavorite
          ? {
                text: 'Favorite',
                icon: <Star className="w-3 h-3 fill-current" />,
                cls: 'text-green-600 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-500/10 dark:border-green-500/20',
            }
          : {
                text: 'Mark',
                icon: <Star className="w-3 h-3" />,
                cls: 'text-slate-500 bg-slate-100 border-slate-200 dark:text-slate-400 dark:bg-slate-700/40 dark:border-slate-600',
            }

    return (
        <div ref={wrapRef} className="relative shrink-0">
            <button
                type="button"
                disabled={loading}
                onClick={() => setMenuOpen((o) => !o)}
                className={cn(
                    'inline-flex items-center gap-1 h-5 pl-1.5 pr-1 rounded border text-[11px] font-semibold transition-colors cursor-pointer disabled:opacity-60',
                    chip.cls
                )}
            >
                {chip.icon}
                {chip.text}
                <ChevronDown className="w-3 h-3 opacity-60" />
            </button>

            {menuOpen && (
                <div className="absolute z-50 top-full left-0 mt-1 w-40 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-slate-800">
                    {!isFavorite && (
                        <button
                            type="button"
                            onClick={() => {
                                setMenuOpen(false)
                                applyStatus(BrokerType.FAVORITE)
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-slate-700 transition-colors hover:bg-green-50 hover:text-green-600 dark:text-slate-300 dark:hover:bg-green-500/10 dark:hover:text-green-400"
                        >
                            <Star className="w-3.5 h-3.5" />
                            Add to favorites
                        </button>
                    )}
                    {!isBlacklisted && (
                        <button
                            type="button"
                            onClick={() => {
                                setMenuOpen(false)
                                setConfirmOpen(true)
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-slate-700 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-300 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                        >
                            <Ban className="w-3.5 h-3.5" />
                            Block broker
                        </button>
                    )}
                    {(isFavorite || isBlacklisted) && (
                        <button
                            type="button"
                            onClick={() => {
                                setMenuOpen(false)
                                applyStatus(null)
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Remove status
                        </button>
                    )}
                </div>
            )}

            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} size="sm">
                <ModalHeader onClose={() => setConfirmOpen(false)}>Block broker</ModalHeader>
                <ModalBody>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        Add <span className="font-semibold text-slate-900 dark:text-slate-100">{label}</span> to your blacklist? Their loads will be hidden from
                        your searches unless you choose to show blocked companies.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        loading={loading}
                        icon={<Ban className="w-3.5 h-3.5" />}
                        onClick={async () => {
                            await applyStatus(BrokerType.IN_BLACKLIST)
                            setConfirmOpen(false)
                        }}
                    >
                        Block broker
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    )
}
